import ComingSoon from 'commonComponents/comingSoonPage';
import React, { useEffect } from 'lib/react';
import { useTranslation } from 'lib/translation';
import { LEGACY_PORTAL_LINKS } from 'constants/AppConstants';
import { useDispatch, useSelector } from 'lib/redux';
import {
  EVGrid, EVPaper, EVInputLabel, EVRadio, EVButton,
} from 'lib/components';
import RetryScreen from 'commonComponents/retry-screen';
import { SUPPORT_ROUTE } from 'constants/RouteConstants';
import history from 'store/CreateHistory';
import { UPDATE_TEXT_ALERT_DELIVERY_STATUS, RESET_TEXT_ALERT_PREFERENCES, LOAD_SAVE_TEXT_ALERT_PREFERENCES } from 'constants/ActionTypes';
import Typography from 'commonComponents/typography';
import Banner from 'commonComponents/banner';
import TextAlerSettingsStyles from './TextAlertSettingsStyles';
import { loadTextAlertSettingsPage, closeTextAlertPreferences } from './TextAlertSettings.actions';
import TextAlertSettingsLoader from './components/TextAlertSettingsLoader';
import TextAlertPhoneDetails from './components/TextAlertPhoneDetails.component';
import TextAlertMessage from './components/TextAlertMessage.component';
import { getTextAlertValues } from './TextAlertSettings.utils';

function TextAlertSettings() {
  const { t } = useTranslation();
  const classes = TextAlerSettingsStyles();
  const dispatch = useDispatch();
  const { myevRefreshTextAlert } = useSelector(({ featureFlag }) => ({ myevRefreshTextAlert: featureFlag.myevRefreshTextAlert }));
  const {
    textAlertPreferences, updatedTextAlertPreferences,
    loading, saveLoading, textPreferencesBanner, loadError,
  } = useSelector(({ textAlertSettings }) => textAlertSettings);
  const receiveSMSAlerts = getTextAlertValues(updatedTextAlertPreferences, textAlertPreferences, 'receiveSMSAlerts');
  const handleDeliveryStatusChange = (event) => {
    const { target: { id } } = event;
    dispatch({ type: UPDATE_TEXT_ALERT_DELIVERY_STATUS, status: id === 'receiveSMSAlerts' });
  };

  const handleTextAlertClose = () => dispatch(closeTextAlertPreferences());

  useEffect(() => {
    dispatch(loadTextAlertSettingsPage());
    return () => {
      dispatch({ type: RESET_TEXT_ALERT_PREFERENCES });
    };
  }, []);

  if (!myevRefreshTextAlert) {
    return (
      <ComingSoon
        pageTitle={t('common__text_alert_settings')}
        redirectionLink={LEGACY_PORTAL_LINKS.textAlert}
      />
    );
  }

  if (loading) return <TextAlertSettingsLoader />;

  if (loadError) return <RetryScreen handleRetryButtonClick={() => { dispatch(loadTextAlertSettingsPage()); }} />;

  return (
    <>
      {textPreferencesBanner?.open && (
        <Banner
          severity={textPreferencesBanner.severity}
          handleAlertClose={handleTextAlertClose}
          message={textPreferencesBanner.message}
        />
      )}
      <EVGrid className={classes.textAlertSettingsContainer}>
        <EVPaper className={classes.textAlertSettingsPaper}>
          <TextAlertPhoneDetails />
          <TextAlertMessage />
          <EVGrid className={classes.textAlertDeliveryContainer}>
            <Typography fontColor="charcoal-800">
              {t('text_alert_settings__select_delivery_status')}
            </Typography>
            <EVGrid className={classes.deliveryStatusTypesContainer}>
              <EVGrid container alignItems="center" style={{ gap: 8, width: 'fit-content' }}>
                <EVRadio
                  id="receiveSMSAlerts"
                  key="receiveSMSAlerts"
                  checked={receiveSMSAlerts}
                  onChange={handleDeliveryStatusChange}
                  color="primary"
                  style={{ padding: 0, width: 'fit-content' }}
                  name="receive SMS Alerts"
                />
                <EVInputLabel htmlFor="delivery-radio" style={{ fontSize: '16px', color: 'black', fontWeight: '400' }}>
                  {t('text_alert_settings__send_sms_alerts')}
                </EVInputLabel>
              </EVGrid>
              <EVGrid container alignItems="center" style={{ gap: 8, width: 'fit-content' }}>
                <EVRadio
                  id="pauseSMSAlerts"
                  key="pauseSMSAlerts"
                  checked={!receiveSMSAlerts}
                  onChange={handleDeliveryStatusChange}
                  color="primary"
                  style={{ padding: 0, width: 'fit-content' }}
                  name="pause SMS Alerts"
                />
                <EVInputLabel htmlFor="delivery-radio" style={{ fontSize: '16px', color: 'black', fontWeight: '400' }}>
                  {t('text_alert_settings__pause_sms_alerts')}
                </EVInputLabel>
              </EVGrid>
            </EVGrid>

          </EVGrid>
          <EVGrid className={classes.messageAndButtonsContainer}>
            <EVGrid className={classes.carrierMessageContainer}>
              <Typography variant="label-400">
                {t('text_alert_settings__note_message_1')}
              </Typography>
              <Typography
                variant="label-400"
                style={{ display: ' flex', gap: '8px', alignItems: 'center' }}
              >
                {t('text_alert_settings__note_message_2')}
                <EVButton
                  data-testid="text-alerts-let-us-know-btn"
                  onClick={() => history.push(SUPPORT_ROUTE)}
                  className={classes.redirectionButtonStyles}
                >
                  {t('text_alert_settings__let_us_know')}
                </EVButton>
              </Typography>
            </EVGrid>
            <EVGrid className={classes.buttonsContainer}>
              <EVButton
                id="save-btn"
                variant="contained"
                className={classes.saveButton}
                color="primary"
                type="submit"
                loading={saveLoading}
                onClick={() => dispatch({ type: LOAD_SAVE_TEXT_ALERT_PREFERENCES })}
              >
                {t('common__save')}
              </EVButton>
              <EVButton
                id="cancel-btn"
                disabled={Object.keys(updatedTextAlertPreferences).length === 0}
                color="primary"
                variant="outlined"
                className={classes.cancelButton}
                onClick={() => dispatch({ type: RESET_TEXT_ALERT_PREFERENCES })}
              >
                {t('common_cancel')}
              </EVButton>
            </EVGrid>
          </EVGrid>
        </EVPaper>
      </EVGrid>
    </>
  );
}

export default TextAlertSettings;
